import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Plus, Edit, Trash2, ArrowRight, Mail, Phone, Search, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';

const STATUS_OPTIONS = [
  { value: 'new', label: 'Novo', color: 'bg-blue-100 text-blue-800' },
  { value: 'qualified', label: 'Qualificado', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'proposal_sent', label: 'Proposta Enviada', color: 'bg-purple-100 text-purple-800' },
  { value: 'negotiating', label: 'Em Negociação', color: 'bg-amber-100 text-amber-800' },
  { value: 'active', label: 'Cliente Ativo', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'lost', label: 'Perdido', color: 'bg-gray-200 text-gray-700' },
];

const SOURCE_OPTIONS = [
  { value: 'site', label: 'Site (formulário)' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'indication', label: 'Indicação' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'event', label: 'Evento' },
  { value: 'other', label: 'Outros' },
];

const EMPTY_FORM = {
  name: '',
  company_name: '',
  email: '',
  phone: '',
  whatsapp: '',
  position: '',
  source: 'site',
  status: 'new',
  notes: '',
};

const StatusBadge = ({ status }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${opt.color}`}>
      {opt.label}
    </span>
  );
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await axios.get(`${API_URL}/api/leads/`, {
        withCredentials: true,
        params,
      });
      setLeads(data);
    } catch (error) {
      toast.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, [API_URL, statusFilter, sourceFilter, search]);

  useEffect(() => {
    const t = setTimeout(fetchLeads, 250);
    return () => clearTimeout(t);
  }, [fetchLeads]);

  const submit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.company_name.trim()) {
      toast.error('Preencha nome e empresa');
      return;
    }
    try {
      const payload = { ...formData };
      if (!payload.email) delete payload.email;
      if (editing) {
        await axios.put(`${API_URL}/api/leads/${editing.id}`, payload, { withCredentials: true });
        toast.success('Lead atualizado');
      } else {
        await axios.post(`${API_URL}/api/leads/`, payload, { withCredentials: true });
        toast.success('Lead criado');
      }
      setDialogOpen(false);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (lead) => {
    setEditing(lead);
    setFormData({
      name: lead.name || '',
      company_name: lead.company_name || '',
      email: lead.email || '',
      phone: lead.phone || '',
      whatsapp: lead.whatsapp || '',
      position: lead.position || '',
      source: lead.source || 'site',
      status: lead.status || 'new',
      notes: lead.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/leads/${id}`, { withCredentials: true });
      toast.success('Lead removido');
      fetchLeads();
    } catch (error) {
      toast.error('Erro ao remover');
    } finally {
      setDeleteId(null);
    }
  };

  const handleConvert = async (lead, payload) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/leads/${lead.id}/convert`,
        payload,
        { withCredentials: true }
      );
      toast.success(`Lead convertido em cliente. ID: ${data.company_id}`);
      setConvertLead(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao converter');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
  };

  const stats = useMemo(() => {
    const counts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s.value]: 0 }), {});
    leads.forEach((l) => {
      if (counts[l.status] !== undefined) counts[l.status] += 1;
    });
    return counts;
  }, [leads]);

  return (
    <AdminSidebar
      title="Leads"
      subtitle="Funil comercial de prospects e clientes em potencial"
    >
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="leads-funnel">
        {STATUS_OPTIONS.map((s) => (
          <Card key={s.value} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded ${s.color}`}>
                {s.label}
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-2">{stats[s.value]}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-4 md:p-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar por nome, empresa ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="leads-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 px-3 rounded-md border border-input bg-white text-sm"
              data-testid="leads-status-filter"
            >
              <option value="all">Todos os status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="h-10 px-3 rounded-md border border-input bg-white text-sm"
              data-testid="leads-source-filter"
            >
              <option value="all">Todas as origens</option>
              {SOURCE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-lead-button">
                  <Plus className="mr-2 h-4 w-4" /> Novo Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editing ? 'Editar Lead' : 'Novo Lead'}</DialogTitle>
                  <DialogDescription>
                    Cadastre um prospect ou atualize o status do funil comercial.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome do contato *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="lead-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Empresa *</Label>
                      <Input
                        id="company_name"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        required
                        data-testid="lead-company-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="position">Cargo</Label>
                      <Input
                        id="position"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">WhatsApp</Label>
                      <Input
                        id="whatsapp"
                        value={formData.whatsapp}
                        onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="source">Origem</Label>
                      <select
                        id="source"
                        value={formData.source}
                        onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm"
                        data-testid="lead-source-select"
                      >
                        {SOURCE_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm"
                        data-testid="lead-status-select"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notas / observações</Label>
                    <Textarea
                      id="notes"
                      rows={3}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancelar</Button>
                    <Button type="submit" data-testid="lead-save-button">{editing ? 'Atualizar' : 'Criar'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Filter size={40} className="mx-auto text-gray-300 mb-3" />
              Nenhum lead encontrado com os filtros aplicados.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contato</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                    <TableCell>
                      <div className="font-medium">{lead.name}</div>
                      {lead.position && <div className="text-xs text-gray-500">{lead.position}</div>}
                      <div className="flex flex-col gap-0.5 text-xs text-gray-500 mt-1">
                        {lead.email && <span className="inline-flex items-center gap-1"><Mail size={10} />{lead.email}</span>}
                        {(lead.phone || lead.whatsapp) && (
                          <span className="inline-flex items-center gap-1">
                            <Phone size={10} />{lead.phone || lead.whatsapp}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{lead.company_name}</TableCell>
                    <TableCell>
                      <span className="text-xs text-gray-600">
                        {SOURCE_OPTIONS.find((s) => s.value === lead.source)?.label}
                      </span>
                    </TableCell>
                    <TableCell><StatusBadge status={lead.status} /></TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      {!lead.converted_company_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConvertLead(lead)}
                          title="Converter em cliente"
                          data-testid={`convert-${lead.id}`}
                        >
                          <ArrowRight className="h-4 w-4 text-emerald-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(lead)} data-testid={`edit-lead-${lead.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(lead.id)} data-testid={`delete-lead-${lead.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Convert dialog */}
      <ConvertDialog
        lead={convertLead}
        onClose={() => setConvertLead(null)}
        onConvert={handleConvert}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminSidebar>
  );
}

const ConvertDialog = ({ lead, onClose, onConvert }) => {
  const [form, setForm] = useState({
    legal_name: '',
    trade_name: '',
    cnpj_root: '',
    industry: '',
    size: '',
    city: '',
    state: '',
  });

  useEffect(() => {
    if (lead) {
      setForm({
        legal_name: lead.company_name || '',
        trade_name: lead.company_name || '',
        cnpj_root: '',
        industry: '',
        size: '',
        city: '',
        state: '',
      });
    }
  }, [lead]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.legal_name.trim()) {
      toast.error('Razão social é obrigatória');
      return;
    }
    onConvert(lead, form);
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Converter em Cliente</DialogTitle>
          <DialogDescription>
            Este lead se tornará uma Empresa Cliente. Você poderá adicionar filiais depois.
          </DialogDescription>
        </DialogHeader>
        {lead && (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="legal_name">Razão Social *</Label>
                <Input
                  id="legal_name"
                  value={form.legal_name}
                  onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="trade_name">Nome Fantasia</Label>
                <Input
                  id="trade_name"
                  value={form.trade_name}
                  onChange={(e) => setForm({ ...form, trade_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cnpj_root">CNPJ raiz (8 dígitos)</Label>
                <Input
                  id="cnpj_root"
                  placeholder="12345678"
                  value={form.cnpj_root}
                  onChange={(e) => setForm({ ...form, cnpj_root: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="industry">Setor</Label>
                <Input
                  id="industry"
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="size">Porte</Label>
                <select
                  id="size"
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm"
                >
                  <option value="">Selecione...</option>
                  <option value="small">Pequena</option>
                  <option value="medium">Média</option>
                  <option value="large">Grande</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Cidade</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">Estado (UF)</Label>
                <Input
                  id="state"
                  maxLength={2}
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="submit" data-testid="confirm-convert-button">Converter</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
